import { createId } from "@paralleldrive/cuid2";
import { index, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { z } from "zod";
import { createdAt, updatedAt } from "../lib/types";
import { trainingPrograms } from "./training-programs";
import { users } from "./users";

export const standardPoses = [
  { id: "front_double_biceps", label: "Front Double Biceps (Ön Çift Biceps)" },
  { id: "front_lat_spread", label: "Front Lat Spread (Ön Kanat)" },
  { id: "side_chest", label: "Side Chest (Yan Göğüs)" },
  { id: "back_double_biceps", label: "Back Double Biceps (Arka Çift Biceps)" },
  { id: "back_lat_spread", label: "Back Lat Spread (Arka Kanat)" },
  { id: "side_triceps", label: "Side Triceps (Yan Triceps)" },
  { id: "abdominals_thighs", label: "Abdominal & Thighs (Karın ve Bacak)" },
  { id: "most_muscular", label: "Most Muscular" },
  { id: "other", label: "Serbest Poz / Diğer" },
] as const;

export const poseItemSchema = z.object({
  id: z.string().min(1),
  photoKey: z.string().max(300),
  poseTag: z.string().max(50).default("other"),
  notes: z.string().max(300).nullable().default(null),
});

export type PoseItem = z.infer<typeof poseItemSchema>;

export const posingRoutineDataSchema = z.object({
  photos: z.array(poseItemSchema).default([]),
  notes: z.string().max(1000).nullable().default(null),
  pumpScore: z.number().int().min(1).max(5).nullable().optional(),
});

export type PosingRoutineData = z.infer<typeof posingRoutineDataSchema>;

export const poseDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return (
      Number.isFinite(parsed.getTime()) &&
      parsed.toISOString().slice(0, 10) === value
    );
  }, "Geçerli bir tarih girin.");

export const workoutPoses = sqliteTable(
  "workout_poses",
  {
    poseRoutineId: text("pose_routine_id")
      .primaryKey()
      .notNull()
      .$defaultFn(() => createId()),
    userId: text("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    programId: text("program_id")
      .notNull()
      .references(() => trainingPrograms.programId, { onDelete: "cascade" }),
    date: text("date").notNull(), // Format: YYYY-MM-DD
    data: text("data", { mode: "json" }).notNull().$type<PosingRoutineData>(),
    createdAt,
    updatedAt,
  },
  (table) => [
    uniqueIndex("workout_poses_user_date_unique").on(table.userId, table.date),
    index("idx_workout_poses_user_date").on(table.userId, table.date),
  ],
);

export const saveWorkoutPoseRoutineSchema = z.object({
  programId: z.string().min(1),
  date: poseDateSchema,
  data: posingRoutineDataSchema,
});

export type SaveWorkoutPoseRoutine = z.infer<
  typeof saveWorkoutPoseRoutineSchema
>;
